import { useMemo, useState } from 'react';
import axios from 'axios';

import { canMutate } from '@domain/auth/AdminUser';
import { useAuth } from '@app/hooks/useAuth';
import { ADMIN_API_BASE, apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { formatDateTime } from '@app/lib/format';

interface ApiKeyRow {
  id: number;
  account: string;
  account_company: string;   // the key's customer (was always blank: Account has no company_name)
  customer_email: string;
  name: string;
  prefix: string;
  scopes: string[];
  ip_whitelist: string[];    // enforced on every API-key request; empty = any IP
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

const repo = new AdminListRepository<ApiKeyRow>(apiClient, 'api-keys');

async function revoke(row: ApiKeyRow): Promise<boolean> {
  const reason = window.prompt(
    `Revoke "${row.name}" (${row.prefix}…) for ${row.account_company || 'this customer'}? It stops working at once.\nReason (logged):`,
  );
  if (!reason || !reason.trim()) return false;
  try {
    await apiClient.post(`${ADMIN_API_BASE}/api-keys/${row.id}/revoke/`, { reason: reason.trim() });
    return true;
  } catch (e) {
    const body = axios.isAxiosError(e) ? (e.response?.data as { error?: string; detail?: string } | undefined) : undefined;
    alert(body?.error ?? body?.detail ?? (e as Error).message);
    return false;
  }
}

const baseColumns: DataTableColumn<ApiKeyRow>[] = [
  { id: 'name', header: 'Name', accessor: 'name' },
  {
    id: 'account_company', header: 'Customer',
    accessor: (r) => (
      <span>
        {r.account_company || '—'}
        {r.customer_email && <span className="block text-xs text-slate-500">{r.customer_email}</span>}
      </span>
    ),
  },
  { id: 'prefix', header: 'Prefix', accessor: (r) => <code className="font-mono text-xs">{r.prefix}…</code> },
  { id: 'scopes', header: 'Scopes', accessor: (r) => (r.scopes ?? []).join(', ') },
  {
    id: 'ip_whitelist', header: 'Allowed IPs',
    accessor: (r) => ((r.ip_whitelist ?? []).length ? r.ip_whitelist.join(', ') : <span className="text-slate-400">any</span>),
  },
  { id: 'is_active', header: 'Active', accessor: (r) => (r.is_active ? 'Yes' : 'Revoked') },
  { id: 'created_at', header: 'Created', accessor: (r) => new Date(r.created_at).toLocaleDateString() },
  { id: 'last_used_at', header: 'Last Used', accessor: (r) => (r.last_used_at ? formatDateTime(r.last_used_at) : '—') },
];

export function ApiKeysPage() {
  const user = useAuth((st) => st.user);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState('');
  const columns = useMemo<DataTableColumn<ApiKeyRow>[]>(() => (canMutate(user) ? [
    ...baseColumns,
    {
      id: 'actions', header: '',
      accessor: (r) => (r.is_active ? (
        <button
          type="button"
          className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-700"
          onClick={async (e) => { e.stopPropagation(); if (await revoke(r)) setReload((n) => n + 1); }}
        >
          Revoke
        </button>
      ) : null),
    },
  ] : baseColumns), [user]);
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (search) f.search = search;
    return f;
  }, [search]);
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-lg font-semibold">API Keys</h2>
        <input
          type="search"
          placeholder="Search name, prefix or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64 rounded border px-2 py-1 text-sm"
        />
      </div>
      <DataTable<ApiKeyRow>
        key={reload}
        columns={columns}
        fetcher={(p) => repo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No API keys issued.</span>}
      />
    </section>
  );
}
